import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./AddMetricModal.css";

const TONE_OPTIONS = [
  { value: "success", label: "Vert", preview: "rgba(175,207,167,0.38)" },
  { value: "danger",  label: "Rouge", preview: "rgba(250,190,190,0.42)" },
  { value: "warning", label: "Jaune", preview: "rgba(254,215,130,0.42)" },
  { value: "low",     label: "Bleu",  preview: "rgba(71,176,222,0.22)" },
  { value: "neutral", label: "Gris",  preview: "rgba(255,255,255,0.55)" },
];

const EMOJI_OPTIONS = ["💊", "🩺", "🧬", "🩸", "💉", "🫀", "🫁", "🧠", "🦷", "👁️", "🌡️", "⚡"];

export default function AddMetricModal({ open, onClose, onAdd, simple = false }) {
  const { t } = useTranslation("neurologue");
  const [label, setLabel]   = useState("");
  const [value, setValue]   = useState("");
  const [unit, setUnit]     = useState("");
  const [tone, setTone]     = useState("low");
  const [emoji, setEmoji]   = useState("💊");
  const [error, setError]   = useState("");
  const inputRef            = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setLabel(""); setValue(""); setUnit(""); setTone("low"); setEmoji("💊"); setError("");
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const handleAdd = () => {
    if (!label.trim()) {
      setError(simple
        ? t("vitals.metricModal.labelRequired", { defaultValue: "Le nom de la mesure est requis." })
        : "Le nom de la métrique est requis.");
      return;
    }
    if (!value.trim()) {
      setError(simple
        ? t("vitals.metricModal.valueRequired", { defaultValue: "La valeur est requise." })
        : "La valeur est requise.");
      return;
    }
    onAdd({ label: label.trim(), value: value.trim(), unit: unit.trim(), tone, emoji });
    onClose();
  };

  if (!open) return null;

  return (
    <div className="amm-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className={`amm-panel${simple ? " amm-panel--simple" : ""}`} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="amm-header">
          <div className="amm-header-left">
            {!simple && <span className="amm-header-icon">✦</span>}
            <div>
              <h2 className="amm-title">
                {simple
                  ? t("vitals.metricModal.title", { defaultValue: "Nouvelle mesure" })
                  : "Nouvelle métrique"}
              </h2>
              <p className="amm-subtitle">
                {simple
                  ? t("vitals.metricModal.subtitle", { defaultValue: "Ajoutez une mesure personnalisée" })
                  : "Ajoutez un indicateur personnalisé"}
              </p>
            </div>
          </div>
          <button
            className="amm-close"
            onClick={onClose}
            aria-label={simple
              ? t("vitals.metricModal.close", { defaultValue: "Fermer" })
              : "Fermer"}
          >
            ×
          </button>
        </div>

        {/* Emoji picker */}
        {!simple && (
          <div className="amm-field">
            <label className="amm-label">Icône</label>
            <div className="amm-emoji-grid">
              {EMOJI_OPTIONS.map((em) => (
                <button
                  key={em}
                  type="button"
                  className={`amm-emoji-btn ${emoji === em ? "selected" : ""}`}
                  onClick={() => setEmoji(em)}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Label */}
        <div className="amm-field">
          <label className="amm-label" htmlFor="amm-label-input">
            {simple
              ? t("vitals.metricModal.name", { defaultValue: "Nom de la mesure *" })
              : "Nom de la métrique *"}
          </label>
          <input
            id="amm-label-input"
            ref={inputRef}
            className={`amm-input ${error && !label.trim() ? "amm-input--error" : ""}`}
            type="text"
            placeholder={simple
              ? t("vitals.metricModal.namePlaceholder", { defaultValue: "Ex. : douleur, sommeil…" })
              : "ex : Cholestérol, Température…"}
            value={label}
            onChange={(e) => { setLabel(e.target.value); setError(""); }}
            maxLength={32}
          />
        </div>

        {/* Value + Unit row */}
        <div className="amm-row">
          <div className="amm-field amm-field--grow">
            <label className="amm-label" htmlFor="amm-value-input">
              {simple
                ? t("vitals.metricModal.value", { defaultValue: "Valeur *" })
                : "Valeur *"}
            </label>
            <input
              id="amm-value-input"
              className={`amm-input ${error && !value.trim() ? "amm-input--error" : ""}`}
              type="text"
              placeholder={simple
                ? t("vitals.metricModal.valuePlaceholder", { defaultValue: "Ex. : 180" })
                : "ex : 180"}
              value={value}
              onChange={(e) => { setValue(e.target.value); setError(""); }}
              maxLength={20}
            />
          </div>
          <div className="amm-field amm-field--unit">
            <label className="amm-label" htmlFor="amm-unit-input">
              {simple
                ? t("vitals.metricModal.unit", { defaultValue: "Unité" })
                : "Unité"}
            </label>
            <input
              id="amm-unit-input"
              className="amm-input"
              type="text"
              placeholder="mg/dL"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              maxLength={12}
            />
          </div>
        </div>

        {/* Tone selector */}
        {!simple && (
          <div className="amm-field">
            <label className="amm-label">Couleur</label>
            <div className="amm-tone-row">
              {TONE_OPTIONS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  className={`amm-tone-btn ${tone === t.value ? "selected" : ""}`}
                  style={{ "--tone-color": t.preview }}
                  onClick={() => setTone(t.value)}
                  title={t.label}
                >
                  <span className="amm-tone-swatch" />
                  <span className="amm-tone-name">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Preview */}
        <div className="amm-preview-wrap">
          <span className="amm-preview-label">
            {simple
              ? t("vitals.metricModal.preview", { defaultValue: "Aperçu" })
              : "Aperçu"}
          </span>
          <div
            className="amm-preview-card"
            style={{
              background: simple ? undefined : TONE_OPTIONS.find((t) => t.value === tone)?.preview,
            }}
          >
            {!simple && <span className="amm-preview-emoji">{emoji}</span>}
            <div className="amm-preview-info">
              <span className="amm-preview-val">{value || "--"} <span className="amm-preview-unit">{unit}</span></span>
              <span className="amm-preview-name">
                {label || (simple
                  ? t("vitals.metricModal.nameFallback", { defaultValue: "Nom de la mesure" })
                  : "Nom de la métrique")}
              </span>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && <p className="amm-error" role="alert">{error}</p>}

        {/* Actions */}
        <div className="amm-actions">
          <button type="button" className="amm-btn amm-btn--cancel" onClick={onClose}>
            {simple
              ? t("vitals.metricModal.cancel", { defaultValue: "Annuler" })
              : "Annuler"}
          </button>
          <button type="button" className="amm-btn amm-btn--add" onClick={handleAdd}>
            {simple
              ? t("vitals.metricModal.add", { defaultValue: "Ajouter" })
              : "✦ Ajouter"}
          </button>
        </div>

      </div>
    </div>
  );
}